import React, { useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import WSButton from "../Components/Button/WSButton";
import WSTextField, { TextType } from "../Components/TextBox/WSTextBox";
import imgmetro from "../images/metrologin.svg";
import { callAPI, clearRequest, Method } from "../commonFunctions/CommonFunctions";
import { useSnackbar } from "../Components/Snackbar/SnackBarContext";
import backImg from "../images/background.svg";
import logo from "../images/logo.svg";

interface LoginFormProps {
  onLogin: (token: string, refreshToken: string, name: string, email: string, role: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = React.memo(({ onLogin }) => {
  const backImgStyle = `url(${backImg})`;
  const { control, register, handleSubmit, formState: { errors } } = useForm();
  const { showMessage } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const handleData = async (data: any) => {
    try {
      const request = {
        email: data.email,
        password: data.password,
      };
      const newRequest = clearRequest(request);
      setLoading(true);

      const response: any = await callAPI("auth/login", Method.POST, newRequest);

      if (response && response?.jwtToken) {
        onLogin(response.jwtToken, response.refreshToken, response.email, response.name, response.role);
        showMessage("Logged in successfully!", "success");
      }

    } catch (e: any) {
      const message = e?.response?.data?.message || e.message || "An unexpected error occurred.";
      showMessage(message, "error");
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url(${backImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.1,
        },
      }}
    >
      <Grid
        container
        sx={{
          maxWidth: 1200,
          width: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          position: 'relative',
          zIndex: 1,
        }}
      >

        {/* Left Side - Image with Enhanced Design */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #000080 0%, #000080 100%)',
            position: 'relative',
            overflow: 'hidden',
            padding: 4,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              color: 'white',
            }}
          >
            <img
              src={imgmetro}
              alt="Illustration"
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))',
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease',
                // '&:hover': {
                //   transform: 'translateY(-5px)',
                // },
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                marginTop: 4,
                marginBottom: 2,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Welcome Back!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.9,
                fontSize: '1.1rem',
                maxWidth: 400,
                margin: '0 auto',
              }}
            >
              Sign in to access your dashboard and manage your account efficiently.
            </Typography>
          </Box>

          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
              zIndex: 1,
            }}
          />
        </Grid>

        {/* Right Side - Enhanced Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: { xs: 4, md: 6 },
            backgroundColor: '#ffffff',
            minHeight: { md: '600px' },
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              textAlign: 'center',
              marginBottom: 4,
              animation: 'fadeInUp 0.6s ease-out',
            }}
          >
            <img
              src={logo}
              alt="Company Logo"
              style={{
                height: '60px',
                width: 'auto',
                marginBottom: 2,
              }}
            />
          </Box>

          <Box
            sx={{
              animation: 'fadeInUp 0.8s ease-out 0.2s both',
            }}
          >
            <form onSubmit={handleSubmit(handleData)}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3436',
                    marginBottom: 1,
                    background: 'linear-gradient(135deg, #000080 0%, #000080 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Welcome
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#000000',
                    fontSize: '1.1rem',
                  }}
                >
                  Sign in to continue to your account
                </Typography>
              </Box>

              {/* Login Fields */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <WSTextField
                    label="Email Address"
                    {...register("email", {
                      required: "Email address is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })}
                    id="email"
                    name="email"
                    control={control}
                    labelType="Normal"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 12px rgba(68, 127, 64, 0.1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 12px rgba(68, 127, 64, 0.2)',
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#e0e0e0',
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#000080',
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#000080',
                        borderWidth: 2,
                      },
                    }}
                  />

                </Grid>

                <Grid item xs={12}>
                  <WSTextField
                    label="Password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters"
                      }
                    })}
                    id="password"
                    name="password"
                    control={control}
                    labelType="Normal"
                    type={TextType.Password}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 12px rgba(68, 127, 64, 0.1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 12px rgba(68, 127, 64, 0.2)',
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#e0e0e0',
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#000080',
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#000080',
                        borderWidth: 2,
                      },
                    }}
                  />

                </Grid>

                {/* Remember Me & Forgot Password */}
                {/* <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 2
                  }}>
                    Uncomment if you have remember me functionality
                  <FormControlLabel
                    control={<Checkbox sx={{ color: '#000080' }} />}
                    label="Remember me"
                    sx={{ color: '#636e72' }}
                  />
                 
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000080',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Forgot Password?
                    </Typography>
                  </Box>
                </Grid> */}

                {/* Login Button */}
                <Grid item xs={12}>
                  <WSButton
                    id="login"
                    label={loading ? "Signing In..." : "Sign In"}
                    size="large"
                    variant="contained"
                    type="submit"
                    loading={loading}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      paddingY: 1.75,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #000080 0%, #000080 100%)',
                      boxShadow: '0 8px 20px rgba(68, 127, 64, 0.3)',
                      "&:hover": {
                        background: 'linear-gradient(135deg, #000080 0%, #000080 100%)',
                        boxShadow: '0 12px 25px rgba(68, 127, 64, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      "&:active": {
                        transform: 'translateY(0)',
                      },
                      color: "#fff",
                      textTransform: "none",
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transition: 'left 0.5s ease',
                      },
                      '&:hover::before': {
                        left: '100%',
                      },
                    }}
                  />
                </Grid>

                {/* Divider */}
                {/* <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginY: 3
                  }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        paddingX: 2,
                        color: '#636e72',
                        fontWeight: 500
                      }}
                    >
                      Or
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                </Grid> */}

                {/* Additional Links */}
                {/* <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      color: '#636e72',
                      '& a': {
                        color: '#000080',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      },
                    }}
                  >
                    Don't have an account? <a href="/signup">Create one here</a>
                  </Typography>
                </Grid> */}
              </Grid>
            </form>
          </Box>
        </Grid>
      </Grid>

      {/* Add CSS animations */}
      <style>{`
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}</style>
    </Box>
  );
});
